import { useState } from 'react';

export default function InteriorDisplay() {
  const [selectedSpace, setSelectedSpace] = useState(0);

  const interiorSpaces = [
    {
      title: 'Executive Office',
      description: 'Spacious desk layout with ergonomic furniture, natural light from panoramic windows, and integrated storage solutions',
      image: '/images/4373686ea26b.webp',
      features: [
        {
          icon: 'ri-layout-line',
          title: 'Ergonomic workspace',
          description: 'Designed for comfort and productivity with adjustable furniture'
        },
        {
          icon: 'ri-sun-line',
          title: 'Natural lighting',
          description: 'Panoramic windows providing abundant natural daylight'
        },
        {
          icon: 'ri-archive-line',
          title: 'Built-in storage',
          description: 'Integrated storage solutions for organised workspace'
        },
        {
          icon: 'ri-plug-line',
          title: 'Cable management',
          description: 'Hidden cable routing for clean and professional appearance'
        }
      ]
    },
    {
      title: 'Meeting Room',
      description: 'Professional meeting setup with conference table, presentation display, and acoustic treatment for clear communication',
      image: '/images/ai-contemporary-meeting-room-interior-with--f96d2d.webp',
      features: [
        {
          icon: 'ri-group-line',
          title: 'Conference seating',
          description: 'Comfortable seating arrangement for productive meetings'
        },
        {
          icon: 'ri-tv-line',
          title: 'AV equipment ready',
          description: 'Pre-installed mounting and connections for displays'
        },
        {
          icon: 'ri-volume-mute-line',
          title: 'Acoustic panels',
          description: 'Sound dampening for clear audio and privacy'
        },
        {
          icon: 'ri-pencil-ruler-2-line',
          title: 'Whiteboard wall',
          description: 'Large writable surface for brainstorming sessions'
        }
      ]
    },
    {
      title: 'Creative Studio',
      description: 'Open workspace designed for collaboration and creativity, with flexible furniture and inspiring atmosphere',
      image: '/images/cb143ac9f19c.webp',
      features: [
        {
          icon: 'ri-layout-grid-line',
          title: 'Flexible layout',
          description: 'Modular furniture that adapts to different work styles'
        },
        {
          icon: 'ri-team-line',
          title: 'Collaborative space',
          description: 'Open design encouraging teamwork and interaction'
        },
        {
          icon: 'ri-lightbulb-line',
          title: 'Inspiring design',
          description: 'Creative atmosphere that stimulates innovation'
        },
        {
          icon: 'ri-function-line',
          title: 'Multi-purpose area',
          description: 'Versatile space for various creative activities'
        }
      ]
    },
    {
      title: 'Reception Area',
      description: 'Welcoming entrance space with reception desk, waiting area, and professional branding opportunities',
      image: '/images/90ab375dce97.webp',
      features: [
        {
          icon: 'ri-customer-service-line',
          title: 'Reception desk',
          description: 'Professional front desk for visitor management'
        },
        {
          icon: 'ri-armchair-line',
          title: 'Waiting seating',
          description: 'Comfortable seating area for guests'
        },
        {
          icon: 'ri-palette-line',
          title: 'Branding space',
          description: 'Display areas for company identity and messaging'
        },
        {
          icon: 'ri-star-line',
          title: 'Professional ambiance',
          description: 'Polished environment making strong first impressions'
        }
      ]
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 w-full">
      <div className="max-w-7xl mx-auto w-full">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-thin mb-4 sm:mb-6 px-4">
            Interior <span className="text-gray-300">Excellence</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto px-4">
            Thoughtfully designed spaces that inspire productivity
          </p>
        </div>

        {/* Main Display */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 mb-8 sm:mb-12 lg:mb-16 w-full">
          {/* Large Image */}
          <div className="relative w-full h-64 sm:h-80 md:h-96 lg:h-[600px] rounded-2xl sm:rounded-3xl overflow-hidden group">
            <img 
              src={interiorSpaces[selectedSpace].image}
              alt={interiorSpaces[selectedSpace].title}
              className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
            <div className="absolute bottom-6 sm:bottom-8 left-6 sm:left-8 right-6 sm:right-8">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-white mb-2 sm:mb-3 break-words">{interiorSpaces[selectedSpace].title}</h3>
              <p className="text-sm sm:text-base lg:text-lg text-gray-300 break-words">{interiorSpaces[selectedSpace].description}</p>
            </div>
          </div>

          {/* Features List */}
          <div className="space-y-4 sm:space-y-6 w-full">
            {interiorSpaces[selectedSpace].features.map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:border-gray-500/30 hover:shadow-[0_0_20px_rgba(148,163,184,0.15)] transition-all w-full">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-500/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${feature.icon} text-lg sm:text-xl text-gray-300`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base sm:text-lg font-medium text-white mb-1 sm:mb-2 break-words">{feature.title}</h4>
                    <p className="text-xs sm:text-sm text-gray-400 break-words">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Space Selector */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 w-full">
          {interiorSpaces.map((space, index) => (
            <div
              key={index}
              onClick={() => setSelectedSpace(index)}
              className={`relative rounded-xl sm:rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 w-full ${
                selectedSpace === index
                  ? 'ring-2 ring-gray-400 shadow-[0_0_30px_rgba(148,163,184,0.3)]'
                  : 'ring-1 ring-white/10 hover:ring-gray-500/50'
              }`}
            >
              <div className="relative w-full h-32 sm:h-40">
                <img 
                  src={space.image}
                  alt={space.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              </div>
              <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4">
                <h4 className="text-sm sm:text-base font-medium text-white break-words">{space.title}</h4>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}